import axios from 'axios';
import { store } from '../redux/store';
import { logout } from '../redux/slice/authSlice';

// Base API configuration (ensure /api suffix for backend routes).
// Strip trailing slashes so `https://host/` + `/api` does not become `//api` (403 on some proxies).
const rawBase = process.env.REACT_APP_API_URL || 'http://localhost:5002';
const envUrl = String(rawBase).replace(/\/+$/, '');
const envPath = (() => {
  try {
    return new URL(envUrl).pathname;
  } catch {
    return envUrl;
  }
})();
const API_BASE_URL = /(^|\/)api$/.test(envPath) ? envUrl : `${envUrl}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token (supports both 'token' and 'authToken')
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const msg = error.response?.data?.error ?? error.response?.data?.message;
    // Only treat this as "your session is invalid" when the failed request actually
    // carried a bearer token — a bare login attempt also 401s on wrong credentials,
    // which has nothing to do with an existing session and shouldn't force-reload.
    const hadAuthHeader = Boolean(error.config?.headers?.Authorization);
    const invalidToken =
      hadAuthHeader &&
      (status === 401 ||
        (status === 403 && typeof msg === 'string' && /invalid|expired/i.test(msg)));

    if (invalidToken) {
      store.dispatch(logout());
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      window.location.href = '/';
    }

    // Log error for debugging
    console.error('API Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method,
    });

    return Promise.reject(error);
  }
);

export default apiClient;

// Export common HTTP methods for convenience
export const api = {
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data = {}, config = {}) => apiClient.post(url, data, config),
  put: (url, data = {}, config = {}) => apiClient.put(url, data, config),
  patch: (url, data = {}, config = {}) => apiClient.patch(url, data, config),
  delete: (url, config = {}) => apiClient.delete(url, config),
};

// Export the axios instance for custom usage
export { apiClient };
